import { LayoutImportConverterLocationFragment } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Paths } from 'type-fest';
import { DATASET_VIEW_PAGE_SIZE } from '../../../../common/constants';
import { DatasetDataViewProps } from '../../../../common/types';
import { Button } from '../../../../components/actions/Button';
import DatasetTable from '../../../../components/DatasetTable';
import * as Icon from '../../../../components/icons';
import { Container } from '../../../../components/layout/ContainerFlex';
import { Spacer } from '../../../../components/layout/Spacer';
import { ConvertedLocationFeature } from '../converter.serializable.model';
import {
  converterLocationsTableState,
  converterMultiSelectedLocationsIds,
  converterMultiSelectLocationIdSetter,
  converterPatchedLocations,
} from '../store/converter.state';
import ConverterUpdateLocationsTableAction from './ConverterUpdateLocationsTableAction';
import useConverterLocationTableConfig from './useConverterLocationTableConfig';

const ConverterLocationTable: React.FC<
  DatasetDataViewProps<ConvertedLocationFeature>
> = props => {
  const { t } = useTranslation('designer');

  const data = _.values(useRecoilValue(converterPatchedLocations));
  const [tableState, setTableState] = useRecoilState(
    converterLocationsTableState,
  );
  const columnsConfig = useConverterLocationTableConfig();
  const [rowsCount, setRowsCount] = useState(DATASET_VIEW_PAGE_SIZE);
  const [multiSelectedLocationsIds, setMultiSelectedLocationsIds] =
    useRecoilState(converterMultiSelectedLocationsIds);
  const setMultiSelectedLocationId = useSetRecoilState(
    converterMultiSelectLocationIdSetter,
  );
  const [showUpdatePanel, setShowUpdatePanel] = useState(false);

  function onLoadNext() {
    setRowsCount(rowsCount + DATASET_VIEW_PAGE_SIZE);
  }

  const sv = tableState.searchValues;
  const fieldsWithFilter = _(columnsConfig)
    .filter(c => c.hasFilter)
    .map(c => c.field)
    .value();

  const contains = (
    row: LayoutImportConverterLocationFragment,
    path: Paths<LayoutImportConverterLocationFragment>,
  ) => {
    return (
      _.isEmpty(_.get(sv, path)) ||
      _.get(row, path)
        ?.toString()
        ?.toLowerCase()
        ?.indexOf(_.get(sv, path)?.toLowerCase()) > -1
    );
  };

  const filteredDataAll = _(data).filter(row => {
    return _.every(fieldsWithFilter, p => contains(row, p));
  });

  const filteredData = filteredDataAll.take(rowsCount).value();
  const isLoading = false;
  const totalCount = filteredDataAll.size() ?? 0;

  const checkedRows = _.filter(data, row =>
    _.has(multiSelectedLocationsIds, row.locationId),
  );
  return (
    <Container hasNoScroll>
      <Container col flex1>
        <DatasetTable
          className="flex-1"
          hideScreenTitle={props.hideScreenTitle}
          subtitle={t`Locations`}
          title={props.title}
          isSticky
          id={props?.id}
          columnsConfig={columnsConfig}
          keyFields={['locationId']}
          data={filteredData}
          onLoadNext={onLoadNext}
          onSearch={v => {
            setTableState({ ...tableState, searchValues: v });
          }}
          totalCount={totalCount}
          searchValues={tableState.searchValues}
          isLoading={isLoading}
          checkedValues={checkedRows}
          hasCounter
          hasRowCounter
          hasCheckboxes
          onRowCheck={(row, isSelected) => {
            setMultiSelectedLocationId(row.locationId);
          }}
          onAllCheck={isSelected => {
            if (isSelected) {
              setMultiSelectedLocationsIds(_.keyBy(filteredData, 'locationId'));
            } else {
              setMultiSelectedLocationsIds({});
            }
          }}
          actionSection={
            <div className="flex flex-1 items-center justify-end">
              <Spacer />
              <Button
                label={t`Batch Update`}
                buttonType={showUpdatePanel ? 'loadmore' : 'secondary'}
                buttonSize={'helper'}
                className="!text-xxs rounded-full px-2 py-0.5"
                onPress={() => setShowUpdatePanel(!showUpdatePanel)}
                hasIconAfter
                buttonIcon={
                  showUpdatePanel ? (
                    <Icon.Close className="h-3 w-3 fill-current" />
                  ) : (
                    <Icon.ShowTable className="h-4 w-4 fill-current" />
                  )
                }
              />
            </div>
          }
        />
      </Container>
      {showUpdatePanel && (
        <ConverterUpdateLocationsTableAction selectedLocations={checkedRows} />
      )}
    </Container>
  );
};

export default ConverterLocationTable;
